import React from 'react';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import gettext from '@eb/gettext';
import {
    uploadImageFile,
    updateImage,
    transformToApiCropMask,
    IMAGE_TYPES,
} from '@eb/http';

import { ImageUploader } from '@eb/eds-image-uploader';
import { ValidationFormField } from '@eb/eds-validation-form-field';
import { SVG_PROP_TYPE } from '@eb/eds-vector-image';
import { CROP_MASK_PROP_TYPE } from '@eb/eds-image-cropper';
import { getCroppedImageDataURL } from '@eb/eds-utils';
import { withOverlayControlsHOC } from '@eb/eds-structure';
import { TYPE_COMPLEX } from '@eb/eds-modal';
import { STYLE_FILL, STYLE_NEUTRAL } from '@eb/eds-button';
import { NotificationBar } from '@eb/eds-notification';
import { TYPE_ERROR } from '@eb/eds-notification';

import ConstrainedHeightCropper from './_internal/ConstrainedHeightCropper';
import {
    CROP_DISALLOWED,
    CROP_REQUIRED,
    CROP_INTENTIONS,
    REQUIRED_CROP_CANCELED,
} from './constants';
import {
    transformUploadFailureMessage,
    conditionalDeepCompare,
    pickPropsByTypes,
} from './utils';

const ImageUploaderField = ({ value, ...props }) => (
    <ImageUploader
        {...pickPropsByTypes(props, ImageUploader.propTypes)}
        imageUrl={value}
    />
);

const CropperError = ({ error }) => {
    if (!error) {
        return null;
    }

    return (
        <div className="eds-l-mar-bot-2" data-spec="cropper-modal-error">
            <NotificationBar type={TYPE_ERROR} shouldScrollTo={true}>
                {error}
            </NotificationBar>
        </div>
    );
};

const DEFAULT_CROP_STATE = {
    continueUpload: noop,
    cancelUpload: noop,
    setCroppedImage: noop,
    workingCropMask: undefined,
    showCropper: false,
    cropperImageUrl: undefined,
    isEditingCrop: false,
    cropIsSaving: false,
};

class ImageField extends React.Component {
    static propTypes = {
        /**
         * Updates form field values based on new image field data
         * (from calling `connectImageField` in `@eb/redux-image-field`)
         */
        updateFieldsFromObject: PropTypes.func.isRequired,
        /**
         * Name of the image form field
         * (from calling `connectImageField` in `@eb/redux-image-field`)
         */
        name: PropTypes.string.isRequired,
        /**
         * ID of existing image
         * (from calling `connectImageField` in `@eb/redux-image-field`)
         */
        imageId: PropTypes.string,
        /**
         * URL to image (used in cropping)
         * (from calling `connectImageField` in `@eb/redux-image-field`)
         */
        originalUrl: PropTypes.string,
        /**
         * Crop mask to apply
         * (from calling `connectImageField` in `@eb/redux-image-field`)
         */
        cropMask: CROP_MASK_PROP_TYPE,

        /**
         * The API image type
         */
        imageType: PropTypes.oneOf(IMAGE_TYPES).isRequired,
        /**
         * Alt attribute provided to the underlying image tag.
         */
        imageAlt: translationPropType.isRequired,
        /**
         * Hidden accessibility label
         */
        hiddenLabel: translationPropType.isRequired,
        /**
         * Whether or not cropping is mandatory, optional, or disallowed
         */
        cropIntention: PropTypes.oneOf(CROP_INTENTIONS),
        /**
         * Desired aspect ratio of the resulting crop.
         */
        cropAspectRatio: PropTypes.number,

        /**
         * Primary text shown in the initial uploader state.
         * This should be a call-to-action to the user ("Choose an Image")
         */
        headline: translationPropType,
        /**
         * Secondary text shown in the initial uploader state.
         * This can be longer descriptive text to help the user choose an appropriate image.
         */
        description: translationPropType,
        /**
         * Icon shown above the headline in the initial uploader state.
         */
        iconType: SVG_PROP_TYPE,
        /**
         * The maximum acceptable filesize specified in megabytes.
         * Files larger than this number will be rejected with an error message.
         */
        maxFileSize: PropTypes.number,
        /**
         * A list of acceptable content-types for dropped files.
         * Files of any type other than those in this list will be rejected with an error message.
         */
        allowedContentTypes: PropTypes.arrayOf(PropTypes.string),
        /**
         * The error message shown to the user if they drop an inappropriate filetype.
         * This can be specified via props to provide a more specific actionable message
         * such as "Please choose a JPG, PNG, or GIF"
         */
        contentTypeError: translationPropType,
        /**
         * The amount of spacing for the bottom
         */
        bottomSpacing: PropTypes.number,
        /**
         * Function to determine whether we should
         * display an error given the input state. Has a sensible default.
         */
        shouldDisplayError: PropTypes.func,
        /**
         * Function that's invoked when the user has chosen a file
         */
        onReceiveFile: PropTypes.func,
        /**
         * Function that's invoked when the user clicks "Remove"
         */
        onRemoveImage: PropTypes.func,
        /**
         * Function that's invoked when the user clicks "Crop" button.
         * The "Crop" button is optional will only appear if this
         * function is provided.
         */
        onEditImage: PropTypes.func,
        /**
         * Function that's invoked when the user has chosen a file and returns
         * the status of the ImageUploader process of uploading.
         * Returns 3 possibles strings: 'uploading', 'uploaded', 'failed'.
         */
        onUploadStatusChange: PropTypes.func,

        // From HOCs
        showOverlay: PropTypes.func,
    };

    static defaultProps = {
        cropIntention: CROP_DISALLOWED,
    };

    state = {
        ...DEFAULT_CROP_STATE,
        cropError: undefined,
    };

    shouldComponentUpdate(nextProps, nextState) {
        return (
            !conditionalDeepCompare(this.props, nextProps, 'cropMask') ||
            !conditionalDeepCompare(this.state, nextState, 'workingCropMask')
        );
    }

    componentDidUpdate() {
        const { showOverlay, imageAlt, cropAspectRatio, cropMask } = this.props;
        const { showCropper, cropperImageUrl } = this.state;

        /**
         * We need to set an intention to show the cropper before `cropperImageUrl` is
         * available when uploading a new image.
         * During initial upload:
         *      A synchronous process (user chooses the file) will set `showCropper`.
         *      An async process (loading image URL from local file) will set `cropperImageUrl`.
         * We should only show the modal when both are fulfilled.
         * If crop isn't required, `cropperImageUrl` will be set but no crop is needed.
         * Therefore, we should check both to see if we should show cropper.
         *
         * During edit, both `showCropper` and `cropperImageUrl` will be set at the same time.
         */
        if (showCropper && cropperImageUrl) {
            showOverlay('modal', {
                type: TYPE_COMPLEX,
                title: gettext('Crop Image'),
                onClose: this._handleCropCancel.bind(this),
                onPrimaryAction: this._handleCropSave.bind(this),
                primaryText: gettext('Save'),
                primaryType: STYLE_FILL,
                onSecondaryAction: this._handleCropCancel.bind(this),
                secondaryText: gettext('Cancel'),
                secondaryType: STYLE_NEUTRAL,

                children: (
                    <ConstrainedHeightCropper
                        imageSrc={cropperImageUrl}
                        onCropDone={this._handleCrop.bind(this)}
                        imageAltText={imageAlt}
                        aspectRatio={cropAspectRatio}
                        cropMask={cropMask}
                    />
                ),
            });
        }
    }

    _handleCrop(workingCropMask) {
        this.setState({ workingCropMask });
    }

    _interceptProgressImage(cropperImageUrl, setCroppedImage) {
        this.setState({
            setCroppedImage,
            cropperImageUrl,
        });
    }

    _handleProgressImageLoaded(progressImage) {
        return new Promise(
            this._interceptProgressImage.bind(this, progressImage),
        );
    }

    _interceptUploadForCropIntention(continueUpload, cancelUpload) {
        const { cropIntention } = this.props;

        if (cropIntention !== CROP_REQUIRED) {
            continueUpload();
            return;
        }

        this.setState({
            continueUpload,
            cancelUpload: cancelUpload.bind(null, REQUIRED_CROP_CANCELED),
            showCropper: true,
            isEditingCrop: false,
        });
    }

    _performUpload(file, onProgress, cropMask) {
        const { imageType } = this.props;
        let apiCropMask;

        if (cropMask) {
            apiCropMask = transformToApiCropMask(cropMask);
        }

        return uploadImageFile(file, imageType, onProgress, apiCropMask);
    }

    _handleUploadSuccess(apiImage) {
        const { updateFieldsFromObject } = this.props;
        const { localCroppedImage } = this.state;
        const imageUrl = localCroppedImage || apiImage.url;

        updateFieldsFromObject({
            ...apiImage,
            url: imageUrl,
        });
        return imageUrl;
    }

    _handleReceiveFile(file, onProgress) {
        const { onReceiveFile } = this.props;

        if (onReceiveFile) {
            onReceiveFile();
        }

        return new Promise(this._interceptUploadForCropIntention.bind(this))
            .then(this._performUpload.bind(this, file, onProgress))
            .then(this._handleUploadSuccess.bind(this))
            .catch(transformUploadFailureMessage);
    }

    _handleRemoveImage() {
        const { updateFieldsFromObject, onRemoveImage } = this.props;

        if (onRemoveImage) {
            onRemoveImage();
        }

        updateFieldsFromObject();
        this.setState({
            cropError: undefined,
        });
    }

    _handleCropCancel() {
        const { cancelUpload } = this.state;

        cancelUpload();
        this._resetCropState();
    }

    _handleNewCropSave(cropMask) {
        const { cropperImageUrl, continueUpload, setCroppedImage } = this.state;

        getCroppedImageDataURL(cropperImageUrl, cropMask).then((dataUri) => {
            setCroppedImage(dataUri);
            this.setState({ localCroppedImage: dataUri });
        });
        continueUpload(cropMask);
        this._resetCropState();
    }

    _resetCropState() {
        this.setState(DEFAULT_CROP_STATE);
    }

    _handleEditCrop() {
        const { originalUrl, onEditImage } = this.props;

        if (onEditImage) {
            onEditImage();
        }

        this.setState({
            showCropper: true,
            isEditingCrop: true,
            cropperImageUrl: originalUrl,
            cropError: undefined,
        });
    }

    _handleEditCropSave(cropMask) {
        const { imageId } = this.props;

        this.setState({
            cropIsSaving: true,
            showCropper: false,
        });
        updateImage(imageId, transformToApiCropMask(cropMask))
            .then(this._handleEditCropSaveSuccess.bind(this))
            .catch(this._handleEditCropSaveFailure.bind(this));
    }

    _handleEditCropSaveSuccess(apiImage) {
        const { updateFieldsFromObject } = this.props;

        updateFieldsFromObject(apiImage);
        this._resetCropState();
    }

    _handleEditCropSaveFailure() {
        this.setState({
            cropError: gettext('Unable to save your crop, please try again'),
            cropIsSaving: false,
        });
        this._resetCropState();
    }

    _handleCropSave() {
        const { isEditingCrop, workingCropMask } = this.state;

        if (isEditingCrop) {
            return this._handleEditCropSave(workingCropMask);
        }

        return this._handleNewCropSave(workingCropMask);
    }

    render() {
        const {
            name,
            hiddenLabel,

            headline,
            description,
            iconType,
            maxFileSize,
            allowedContentTypes,
            contentTypeError,
            imageAlt,
            cropIntention,
            bottomSpacing,
            onUploadStatusChange,
            shouldDisplayError,
        } = this.props;
        const { cropError } = this.state;
        let onCrop;

        if (cropIntention !== CROP_DISALLOWED) {
            onCrop = this._handleEditCrop.bind(this);
        }

        return (
            <div>
                <CropperError error={cropError} />
                <ValidationFormField
                    name={`${name}Url`}
                    label={hiddenLabel}
                    hideLabel={true}
                    bottomSpacing={bottomSpacing}
                    shouldDisplayError={shouldDisplayError}
                >
                    <ImageUploaderField
                        onReceiveFile={this._handleReceiveFile.bind(this)}
                        onRemove={this._handleRemoveImage.bind(this)}
                        onProgressImageLoaded={this._handleProgressImageLoaded.bind(
                            this,
                        )}
                        onCrop={onCrop}
                        onUploadStatusChange={onUploadStatusChange}
                        headline={headline}
                        description={description}
                        iconType={iconType}
                        maxFileSize={maxFileSize}
                        allowedContentTypes={allowedContentTypes}
                        contentTypeError={contentTypeError}
                        imageAlt={imageAlt}
                    />
                </ValidationFormField>
            </div>
        );
    }
}

export default withOverlayControlsHOC(ImageField);
